// src/App.tsx

import { createBrowserRouter, RouterProvider, Outlet, ScrollRestoration, Navigate } from 'react-router-dom';

import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";

import React, { useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Browser } from '@capacitor/browser';
import { App as CapacitorApp } from '@capacitor/app';
import Home from './pages/Home';
import TextPage from './pages/TextPage';
import Contact from './pages/Contact';
import DeleteAccount from './pages/DeleteAccount';
import DataRetentionPolicy from './pages/DataRetentionPolicy';
//import ContactAboutServiceProvider from './pages/ContactAboutServiceProvider';

//import Navbar from './components/NavBar';

import { ThemeProvider } from '@mui/material/styles';

import Fade from '@mui/material/Fade';
import { Box, CssBaseline, GlobalStyles } from "@mui/material";

import Footer from './components/Footer';
// import { ToastContainer } from 'react-toastify';

import CookieConsent from './components/CookieConsent';
import ConnectionStatusBanner from './components/ConnectionStatusBanner';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NavBar from './components/NavBar';

import ReferralsAppParent from './referralsApp/ReferralsAppParent';

import {APIUnauthenticatedService, APIUnauthenticatedServiceContext} from './helpers/APICallsUnauthenticated';
import { APIAuthenticatedService } from './helpers/APICallsAuthenticated';
import { ConfigInfoService, ConfigInfoServiceContext  } from './helpers/ConfigInfo';

import Login from './components/Login';
import ReferralsAppAuthenticated from './referralsApp/referralsAppAuthenticated/ReferralsAppAuthenticated';
import SignUp from './pages/SignUp';
import ReferralsReceivedPage from './referralsApp/referralsAppAuthenticated/ReferralsReceivedPage';
import ReferralsSentPage from './referralsApp/referralsAppAuthenticated/ReferralsSentPage';
import ReferralReceivedDetailPage from './referralsApp/referralsAppAuthenticated/ReferralReceivedDetailPage';
import ReferralSentDetailPage from './referralsApp/referralsAppAuthenticated/ReferralSentDetailPage';
import AdminReferralsPage from './referralsApp/referralsAppAuthenticated/AdminReferralsPage';
import AdminGroupTwoReferralsPage from './referralsApp/referralsAppAuthenticated/AdminGroupTwoReferralsPage';
import AdminIndexPage from './referralsApp/referralsAppAuthenticated/AdminIndexPage';
import AdminGroupOneActivityPage from './referralsApp/referralsAppAuthenticated/AdminGroupOneActivityPage';
import AdminGroupTwoActivityPage from './referralsApp/referralsAppAuthenticated/AdminGroupTwoActivityPage';
import AdminReferralConversationAsOffererPage from './referralsApp/referralsAppAuthenticated/AdminReferralConversationAsOffererPage';
import AdminReferralConversationAsReferrerPage from './referralsApp/referralsAppAuthenticated/AdminReferralConversationAsReferrerPage';

import GetSignedUp from './pages/GetSignedUp';
import { AppRuntimeProvider } from './appCore/AppRuntimeReact';

//import { loadGoogleAnalytics } from './helpers/analyticsUtils';
import AboutUsPage from './pages/AboutUs';
import {
  clearStaleNativeLogoutState,
  completeNativeLogin,
  completeNativeLogout,
  consumeQueuedNativeAuthorizeUrl,
  describeNativeAuthUrlForLog,
  getNativeAuthSession,
  isNativeAuth0CallbackUrl,
  isNativeAuth0LogoutUrl,
  markNativeDeepLinkHandled,
  openNativeAuth0Url,
} from './helpers/auth0Capacitor';
import {
  PUSH_LAST_RECEIVED_TOKEN_KEY,
  PUSH_LAST_REGISTERED_TOKEN_KEY,
} from './helpers/pushDeviceAuth';
import { handlePushNotificationAction } from './helpers/pushNotificationNavigation';
import { parseUnreadCount } from './helpers/referralUnread';
import {
  setAndroidReferralBadgeCount,
  syncAndroidReferralBadgeCountFromApi,
} from './helpers/referralBadgeSync';
import { createAppTheme } from './theme/appTheme';

const appTheme = createAppTheme();

const replaceRoute = (targetPath: string) => {
  window.history.replaceState({}, document.title, targetPath);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

const pushRoute = (targetPath: string) => {
  window.history.pushState({}, document.title, targetPath);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

const App: React.FC = () => {  

  const {
    handleRedirectCallback,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();
  const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || '';
  const configInfoService = ConfigInfoService.getInstance();

  const registerPushTokenWithBackend = useCallback(async (deviceToken: string) => {
    if (!deviceToken) {
      return;
    }

    localStorage.setItem(PUSH_LAST_RECEIVED_TOKEN_KEY, deviceToken);

    if (!isAuthenticated) {
      console.log("[push] user not authenticated yet; token cached for later registration");
      return;
    }

    const lastRegisteredToken = localStorage.getItem(PUSH_LAST_REGISTERED_TOKEN_KEY);
    if (lastRegisteredToken === deviceToken) {
      console.log("[push] token already registered with backend; skipping");
      return;
    }

    try {
      const apiService = APIAuthenticatedService.getInstance(
        getAccessTokenSilently,
        getIdTokenClaims,
        configInfoService
      );

      await apiService.init();
      await apiService.registerPushDevice(deviceToken, 'android');
      localStorage.setItem(PUSH_LAST_REGISTERED_TOKEN_KEY, deviceToken);
      console.log("[push] token registered with backend");
    } catch (error: any) {
      const statusCode = error?.response?.status ?? 'unknown';
      // REQ-047: avoid logging Axios error objects because request config can include device tokens.
      console.log(`[push] failed to register token with backend (status: ${statusCode})`);
    }
  }, [configInfoService, getAccessTokenSilently, getIdTokenClaims, isAuthenticated]);

  const updateAndroidBadgeCount = useCallback(
    async (count: number) => {
      await setAndroidReferralBadgeCount(count);
    },
    []
  );

  const updateAndroidBadgeCountFromNotificationData = useCallback(
    async (notificationData?: { unreadCount?: unknown } | null) => {
      const unreadCount = parseUnreadCount(notificationData?.unreadCount);

      if (unreadCount === null) {
        return;
      }

      await updateAndroidBadgeCount(unreadCount);
    },
    [updateAndroidBadgeCount]
  );

  const syncAndroidBadgeCountFromApi = useCallback(async () => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android' || !isAuthenticated) {
      return;
    }

    try {
      const apiService = APIAuthenticatedService.getInstance(
        getAccessTokenSilently,
        getIdTokenClaims,
        configInfoService
      );

      await apiService.init();
      await syncAndroidReferralBadgeCountFromApi(apiService);
    } catch (error) {
      // REQ-049: avoid logging raw API errors from push badge sync paths.
      console.log('[push] failed to sync Android badge count from API');
    }
  }, [
    configInfoService,
    getAccessTokenSilently,
    getIdTokenClaims,
    isAuthenticated,
  ]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const isAndroid = Capacitor.getPlatform() === 'android';

    clearStaleNativeLogoutState();

    let removeListener: (() => Promise<void>) | undefined;
    let removeBrowserFinishedListener: (() => Promise<void>) | undefined;
    let removeResumeListener: (() => Promise<void>) | undefined;
    let browserFinishedTimeoutId: number | undefined;

    const closeAuthBrowser = async () => {
      if (Capacitor.getPlatform() === 'android') {
        return;
      }

      await Browser.close();
    };

    const clearBrowserFinishedCheck = () => {
      if (browserFinishedTimeoutId === undefined) {
        return;
      }

      window.clearTimeout(browserFinishedTimeoutId);
      browserFinishedTimeoutId = undefined;
    };

    const scheduleBrowserFinishedCheck = () => {
      clearBrowserFinishedCheck();

      browserFinishedTimeoutId = window.setTimeout(() => {
        const activeSession = getNativeAuthSession();

        if (!activeSession) {
          return;
        }

        console.log(
          `[auth0] native ${activeSession.intent} browser finished before callback`,
          describeNativeAuthUrlForLog(activeSession.browserUrl)
        );
      }, 1500);
    };

    const checkLaunchUrlForMissedRedirect = async (reason: string) => {
      const launchUrl = await CapacitorApp.getLaunchUrl();

      if (!launchUrl?.url) {
        return;
      }

      if (
        !isNativeAuth0CallbackUrl(launchUrl.url, auth0Domain) &&
        !isNativeAuth0LogoutUrl(launchUrl.url, auth0Domain)
      ) {
        return;
      }

      // REQ-048: native auth callback URLs can include code/state values and must be redacted in logs.
      console.log(`[auth0] checking native launch URL after ${reason}`, describeNativeAuthUrlForLog(launchUrl.url));
      await finishAuthRedirect(launchUrl.url);
    };

    const finishAuthRedirect = async (url: string) => {
      if (markNativeDeepLinkHandled(url)) {
        return;
      }

      clearBrowserFinishedCheck();

      if (isNativeAuth0LogoutUrl(url, auth0Domain)) {
        // REQ-048: native auth URLs can include transaction parameters.
        console.log('[auth0] received native logout URL', describeNativeAuthUrlForLog(url));
        completeNativeLogout();

        const queuedAuthorizeUrl = consumeQueuedNativeAuthorizeUrl();

        await closeAuthBrowser();

        if (queuedAuthorizeUrl) {
          // REQ-048: native authorize URLs include Auth0 transaction parameters.
          console.log('[auth0] reopening queued native authorize URL', describeNativeAuthUrlForLog(queuedAuthorizeUrl));
          window.setTimeout(() => {
            void openNativeAuth0Url(queuedAuthorizeUrl);
          }, 150);
          return;
        }

        replaceRoute('/');
        return;
      }

      if (!isNativeAuth0CallbackUrl(url, auth0Domain)) {
        return;
      }

      // REQ-048: never log the callback code/state values.
      console.log('[auth0] received native callback URL', describeNativeAuthUrlForLog(url));
      completeNativeLogin();

      if (url.includes('error=')) {
        console.log('[auth0] callback returned an error', describeNativeAuthUrlForLog(url));
        await closeAuthBrowser();
        return;
      }

      if (!url.includes('code=') || !url.includes('state=')) {
        console.log('[auth0] callback URL missing code/state', describeNativeAuthUrlForLog(url));
        await closeAuthBrowser();
        return;
      }

      try {
        const result = await handleRedirectCallback(url);
        const targetPath =
          (typeof result?.appState?.returnTo === 'string' && result.appState.returnTo) || '/dashboard';

        if (targetPath.startsWith('http://localhost') || targetPath.startsWith('https://localhost')) {
          const parsed = new URL(targetPath);
          replaceRoute(`${parsed.pathname}${parsed.search}${parsed.hash}`);
          return;
        }

        replaceRoute(targetPath);
      } catch (error) {
        console.error('[auth0] failed to handle native callback', error);
      } finally {
        await closeAuthBrowser();
      }
    };

    const setupAuthRedirectHandler = async () => {
      if (!isAndroid) {
        const browserFinishedListener = await Browser.addListener('browserFinished', () => {
          scheduleBrowserFinishedCheck();
          window.setTimeout(() => {
            void checkLaunchUrlForMissedRedirect('browserFinished');
          }, 150);
        });

        removeBrowserFinishedListener = () => browserFinishedListener.remove();
      }

      const resumeListener = await CapacitorApp.addListener('resume', () => {
        if (isAndroid) {
          scheduleBrowserFinishedCheck();
        }

        window.setTimeout(() => {
          void checkLaunchUrlForMissedRedirect('resume');
        }, 150);
      });

      removeResumeListener = () => resumeListener.remove();

      const launchUrl = await CapacitorApp.getLaunchUrl();
      if (launchUrl?.url) {
        await finishAuthRedirect(launchUrl.url);
      }

      const listener = await CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
        await finishAuthRedirect(url);
      });

      removeListener = () => listener.remove();
    };

    void setupAuthRedirectHandler();

    return () => {
      clearBrowserFinishedCheck();

      if (removeListener) {
        void removeListener();
      }

      if (removeBrowserFinishedListener) {
        void removeBrowserFinishedListener();
      }

      if (removeResumeListener) {
        void removeResumeListener();
      }
    };
  }, [auth0Domain, handleRedirectCallback]);

  useEffect(() => {

    if (!Capacitor.isNativePlatform()) {
      console.log("[push] skipping push setup (not native)");
      return;
    }

    console.log("[push] useEffect fired, platform:", Capacitor.getPlatform(), "native:", Capacitor.isNativePlatform());

    let didCancel = false;

    let h_registration: any = null;
    let h_registrationError: any = null;
    let h_received: any = null;
    let h_action: any = null;

    const initPush = async () => {

      // 1) Install listeners FIRST (before register)
      h_registration = await PushNotifications.addListener("registration", (token) => {
        // REQ-047: do not write push device tokens to browser logs.
        void registerPushTokenWithBackend(token.value);
      });

      h_registrationError = await PushNotifications.addListener("registrationError", (err) => {
        // REQ-049: avoid writing native push payload/error objects to logs.
        console.log("[push] registration error");
      });

      h_received = await PushNotifications.addListener("pushNotificationReceived", (notification) => {
        // REQ-049: notification payloads can include referral context and must not be logged.
        console.log("[push] notification received");
        void updateAndroidBadgeCountFromNotificationData(notification?.data);
      });

      h_action = await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        // REQ-049: notification action payloads can include referral context and must not be logged.
        console.log("[push] notification action");
        void updateAndroidBadgeCountFromNotificationData(action.notification?.data);

        const normalizedRoute = handlePushNotificationAction(action, {
          navigate: pushRoute,
        });

        if (!normalizedRoute) {
          console.log('[push] notification action missing deepLinkPath');
          return;
        }

        console.log('[push] navigating from notification');
      });

      // 2) Permission (Android 13+)
      const permission = await PushNotifications.requestPermissions();

      if (didCancel) {
        return;
      }

      console.log("[push] permission:", permission);

      if (permission.receive !== "granted") {
        console.log("[push] permission not granted");
        return;
      }

      // 3) Now register
      await PushNotifications.register();
      console.log("[push] register() called");
    };

    void initPush();

    return () => {

      didCancel = true;

      if (h_registration) {
        void h_registration.remove();
        h_registration = null;
      }

      if (h_registrationError) {
        void h_registrationError.remove();
        h_registrationError = null;
      }

      if (h_received) {
        void h_received.remove();
        h_received = null;
      }

      if (h_action) {
        void h_action.remove();
        h_action = null;
      }
    };

  }, [registerPushTokenWithBackend, updateAndroidBadgeCountFromNotificationData]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android' || !isAuthenticated) {
      return;
    }

    let removeResumeListener: (() => Promise<void>) | undefined;

    const syncBadgeCount = async () => {
      await syncAndroidBadgeCountFromApi();
    };

    const setupBadgeRefresh = async () => {
      void syncBadgeCount();

      const resumeListener = await CapacitorApp.addListener('resume', () => {
        void syncBadgeCount();
      });

      removeResumeListener = () => resumeListener.remove();
    };

    void setupBadgeRefresh();

    return () => {
      if (removeResumeListener) {
        void removeResumeListener();
      }
    };
  }, [isAuthenticated, syncAndroidBadgeCountFromApi]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return;
    }

    if (isLoading || isAuthenticated) {
      return;
    }

    void updateAndroidBadgeCount(0);
  }, [isAuthenticated, isLoading, updateAndroidBadgeCount]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !isAuthenticated) {
      return;
    }

    const lastReceivedToken = localStorage.getItem(PUSH_LAST_RECEIVED_TOKEN_KEY);
    if (!lastReceivedToken) {
      return;
    }

    void registerPushTokenWithBackend(lastReceivedToken);
  }, [isAuthenticated, registerPushTokenWithBackend]);

  const apiUnauthenticatedService = APIUnauthenticatedService.getInstance(configInfoService);


  // useEffect(() => {
  //   // List the hostname(s) you consider production
  //   const productionDomains = ['inhouseapp.co.uk', 'www.inhouseapp.co.uk'];

  //   if (productionDomains.includes(window.location.hostname)) {
  //     // Your Google Analytics measurement ID
  //     const GA_TRACKING_ID = 'G-8KNW3BX236';
  //     loadGoogleAnalytics(GA_TRACKING_ID);
  //   }
  // }, []);
  const isNativeApp = Capacitor.isNativePlatform();
  
  const router = createBrowserRouter([    
    {
      Component: Layout,
      children: [
        { path: "/", Component: isNativeApp ? () => <Navigate replace to="/dashboard" /> : Home },
        { path: "/home", Component: Home },
        { path: "/dashboard", Component: () => <ReferralsAppParent showIfLoggedIn={<ReferralsAppAuthenticated/>} showIfLoggedOut={<Login/>} /> },
        { path: "/dashboard/referrals/in", Component: () => <ReferralsAppParent showIfLoggedIn={<ReferralsReceivedPage/>} showIfLoggedOut={<Login/>} /> },
        { path: "/dashboard/referrals/out", Component: () => <ReferralsAppParent showIfLoggedIn={<ReferralsSentPage/>} showIfLoggedOut={<Login/>} /> },
        { path: "/dashboard/referrals/in/:referralId", Component: () => <ReferralsAppParent showIfLoggedIn={<ReferralReceivedDetailPage/>} showIfLoggedOut={<Login/>} /> },
        { path: "/dashboard/referrals/out/:referralId", Component: () => <ReferralsAppParent showIfLoggedIn={<ReferralSentDetailPage/>} showIfLoggedOut={<Login/>} /> },
        { path: "/admins", Component: () => <ReferralsAppParent showIfLoggedIn={<AdminIndexPage/>} showIfLoggedOut={<Login/>} /> },
        { path: "/admins/group-1", Component: () => <ReferralsAppParent showIfLoggedIn={<AdminReferralsPage/>} showIfLoggedOut={<Login/>} /> },
        { path: "/admins/group-2", Component: () => <ReferralsAppParent showIfLoggedIn={<AdminGroupTwoReferralsPage/>} showIfLoggedOut={<Login/>} /> },
        { path: "/admins/group-1/activity", Component: () => <ReferralsAppParent showIfLoggedIn={<AdminGroupOneActivityPage/>} showIfLoggedOut={<Login/>} /> },
        { path: "/admins/group-2/activity", Component: () => <ReferralsAppParent showIfLoggedIn={<AdminGroupTwoActivityPage/>} showIfLoggedOut={<Login/>} /> },
        { path: "/admins/referrals/:referralId/as-referrer", Component: () => <ReferralsAppParent showIfLoggedIn={<AdminReferralConversationAsReferrerPage/>} showIfLoggedOut={<Login/>} /> },
        { path: "/admins/referrals/:referralId/as-offerer", Component: () => <ReferralsAppParent showIfLoggedIn={<AdminReferralConversationAsOffererPage/>} showIfLoggedOut={<Login/>} /> },
        { path: "/terms-and-conditions", Component: () => <TextPage filename={'text/terms-and-conditions.txt'} /> },
        { path: "/about-us", Component: () => <AboutUsPage  /> },
        { path: "/contact", Component: Contact},
        // REQ-056: account deletion requests must be available without authentication.
        { path: "/delete-account", Component: DeleteAccount },
        // REQ-057: the policy must be readable without authentication.
        { path: "/data-retention-and-deletion", Component: DataRetentionPolicy },
        //{ path: "/ContactAboutServiceProvider", Component: ContactAboutServiceProvider},
        { path: "/signup", Component: SignUp},
        { path: "/getsignedup", Component: GetSignedUp},
        { path: "*", Component: Home }
      ],
    }
  ]);
  
  function Layout() {
    return (
      <>
        <ThemeProvider theme={appTheme}>
          <CssBaseline />
          <ScrollRestoration />
          <GlobalStyles styles={{ 
            body: { 
              backgroundColor: '#000'
            } 
          }}/>
          <ConfigInfoServiceContext.Provider value={configInfoService}>
            <APIUnauthenticatedServiceContext.Provider value={apiUnauthenticatedService}>
              <AppRuntimeProvider>
                <Fade in={true} timeout={1000}>                  
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    sx={{
                      minHeight: '100vh',
                      width: '100%',
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: ['100%', null, '100%'],
                        height: '100%',
                        backgroundImage: 'url(background.png)',
                        backgroundSize: ['cover', null, '50%'],
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: ['center center', null, 'right bottom'],
                        backgroundAttachment: 'fixed',
                        '@media (max-width: 600px)': {
                          filter: 'brightness(50%)',
                        },
                        zIndex: -1,
                      }}
                    />

                    <Box sx={{ minHeight: '100vh', width: '100%', maxWidth: '1200px', display: 'flex', flexDirection: "column"}}>
                      <NavBar />
                      <Box sx={{ px: 0.5, py: {xs: 0.5, sm: 2, md: 4}, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                        <Outlet />
                      </Box>
                      <Footer/>                  
                    </Box> 
                      
                    <CookieConsent/>
                    <ConnectionStatusBanner />
                  </Box>
                </Fade> 
              
                <ToastContainer
                  position="bottom-center"
                  autoClose={5000}
                  hideProgressBar
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="colored"
                />
              </AppRuntimeProvider>
            </APIUnauthenticatedServiceContext.Provider>
          </ConfigInfoServiceContext.Provider>    
        </ThemeProvider>
      </>
    );
  }
  
  return (<RouterProvider router={router} /> ); 
  
}

export default App;
